import { Injector, Signal, computed, linkedSignal, untracked } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'

import { isPlainObject as _isPlainObject } from 'lodash'

/**
 * A section view is the structure a section service parses out of the theme: nested objects and
 * arrays carrying metadata (names, copy paths, reset flags), where every `fg` key holds the
 * editable values of one group of controls.
 */
const GROUP_KEY = 'fg'

/** Values of one group of controls, the old `FormGroup.value`. */
export type ThemeFormValues = Record<string, any>

/** The section view with every group's values replaced by the group's `FieldTree`. */
export type BoundSectionView<V> = V extends readonly (infer Item)[]
  ? BoundSectionView<Item>[]
  : V extends ThemeFormValues
    ? { [K in keyof V]: K extends typeof GROUP_KEY ? FieldTree<V[K]> : BoundSectionView<V[K]> }
    : V

export interface ThemeSectionForm<V> {
  /** The section view for templates: metadata as plain values, every `fg` as a `FieldTree`. */
  readonly view: Signal<BoundSectionView<V> | null>
  /** The root of the form, over the values of all groups. */
  readonly form: FieldTree<unknown>
}

export interface ThemeSectionFormOptions<V> {
  /** The parsed section view, derived from the theme editor store. */
  readonly view: Signal<V | null>
  /**
   * Pushes an edit to the store. Both arguments are the section view with every `fg` holding its
   * values: `next` after the edit, `previous` before it, so the caller can update only what changed.
   */
  readonly write: (next: V, previous: V) => void
  readonly injector?: Injector
}

const isPlainObject = (node: unknown): node is Record<string, unknown> => _isPlainObject(node)

const containsGroups = (node: unknown): boolean =>
  Array.isArray(node)
    ? node.some(containsGroups)
    : isPlainObject(node) &&
      Object.entries(node).some(([key, value]) => key === GROUP_KEY || containsGroups(value))

/** Deep copy of a group's values with `undefined` turned into `null`: Signal Forms drop `undefined` fields. */
const normalizeValues = (value: unknown): unknown =>
  Array.isArray(value)
    ? value.map(normalizeValues)
    : isPlainObject(value)
      ? Object.fromEntries(Object.entries(value).map(([key, item]) => [key, normalizeValues(item)]))
      : (value ?? null)

/** The form model: the view reduced to the paths that lead to groups, holding only their values. */
export const extractGroupValues = (node: unknown): unknown =>
  Array.isArray(node)
    ? node.map(extractGroupValues)
    : isPlainObject(node)
      ? Object.fromEntries(
          Object.entries(node)
            .filter(([key, value]) => key === GROUP_KEY || containsGroups(value))
            .map(([key, value]) => [
              key,
              key === GROUP_KEY ? normalizeValues(value) : extractGroupValues(value),
            ])
        )
      : {}

/** Rebuilds the view, replacing every group with the node found at the same path in `source`. */
const replaceGroups = (node: unknown, source: unknown): unknown =>
  Array.isArray(node)
    ? node.map((item, index) => replaceGroups(item, source?.[index]))
    : isPlainObject(node)
      ? Object.fromEntries(
          Object.entries(node).map(([key, value]) => [
            key,
            key === GROUP_KEY
              ? source?.[key]
              : containsGroups(value)
                ? replaceGroups(value, source?.[key])
                : value,
          ])
        )
      : node

export const withGroupValues = <V>(view: V, values: unknown): V => replaceGroups(view, values) as V

export const bindFieldTrees = <V>(view: V, tree: FieldTree<unknown>): BoundSectionView<V> =>
  replaceGroups(view, tree) as BoundSectionView<V>

/**
 * Structural equality for form values. The forms library tags array items with an identity symbol,
 * which JSON ignores, so a model that round-trips through the store unchanged compares equal.
 */
export const sameValues = (a: unknown, b: unknown): boolean =>
  a === b || JSON.stringify(a) === JSON.stringify(b)

/** The values a node holds: its groups' values, or the node itself when it is already a group's values. */
const valuesOf = (node: unknown): unknown =>
  containsGroups(node) ? extractGroupValues(node) : normalizeValues(node)

/** Whether any value differs between two views, two parts of them, or two groups' values. */
export const valuesChanged = (next: unknown, previous: unknown): boolean =>
  !sameValues(valuesOf(next), valuesOf(previous))

/**
 * Creates the Signal Form of a theme section.
 *
 * The model is a `linkedSignal` over the section view: it is re-derived whenever the store changes
 * (platform switch, reset, copy, another section's edit), and a field edit goes straight through
 * its custom `set` to `write`, which updates the store. The store stays the single source of truth;
 * the form never holds a second copy of the data. Edits are not debounced: a delayed write would
 * land on whatever platform or display mode is current when it fires, and Save would miss it. The
 * previews are debounced in the store instead.
 */
export function createThemeSectionForm<V>(
  options: ThemeSectionFormOptions<V>
): ThemeSectionForm<V> {
  const model = linkedSignal<V | null, unknown>({
    source: options.view,
    computation: (view) => extractGroupValues(view),
    equal: sameValues,
    set: (next, rawSet) => {
      const previous = untracked(model)
      rawSet(next)
      const view = untracked(options.view)
      if (view && !sameValues(next, previous)) {
        options.write(withGroupValues(view, next), withGroupValues(view, previous))
      }
    },
  })
  const sectionForm = form(model, options.injector ? { injector: options.injector } : {})
  const view = computed(() => {
    const current = options.view()
    return current ? bindFieldTrees(current, sectionForm) : null
  })
  return { view, form: sectionForm }
}
